import { ArrowPathIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { OtsActionButton } from './OtsButton';
import { OtsInlineAlert } from './OtsInlineAlert';

export type OtsStaleDataNoticeProps = {
  /** When the data on screen was loaded, epoch milliseconds. */
  loadedAt?: number;
  /** How old it may get before saying so. */
  staleAfterMinutes?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** What was loaded, e.g. "This day". */
  subject?: string;
  size?: 'compact' | 'default';
  className?: string;
  /** "Now", for stories and tests; the notice keeps its own clock otherwise. */
  now?: number;
};

const minutesAgo = (minutes: number) =>
  minutes < 60 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : `${Math.floor(minutes / 60)} hour${minutes >= 120 ? 's' : ''}`;

/**
 * Says when what is on screen may be out of date, for pages that don't update live: after a while, a quiet notice
 * with how long ago it loaded and a Refresh. It checks its age twice a minute and steps aside while refreshing.
 */
export const OtsStaleDataNotice = ({
  loadedAt,
  staleAfterMinutes = 10,
  onRefresh,
  isRefreshing = false,
  subject = 'This page',
  size = 'compact',
  className,
  now: nowProp,
}: OtsStaleDataNoticeProps) => {
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (nowProp != null) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [nowProp]);
  const now = nowProp ?? clock;
  if (loadedAt == null || isRefreshing) return null;
  const age = Math.floor((now - loadedAt) / 60_000);
  if (age < staleAfterMinutes) return null;

  return (
    <OtsInlineAlert
      tone="warning"
      size={size}
      className={className}
      action={onRefresh && (
        <OtsActionButton size="compact" icon={<ArrowPathIcon/>} onClick={onRefresh}>Refresh</OtsActionButton>
      )}>
      {subject} was loaded {minutesAgo(age)} ago and may be out of date.
    </OtsInlineAlert>
  );
};
